/***
 *
 *   CLIENT ONBOARDING V2 SETTINGS
 *   The staff settings page for the person-first onboarding type. v2 has its
 *   own settings record and its own public link, beside v1 (R3, R4).
 *
 *   GET /api/client-onboarding-v2-settings answers at the top level, not under
 *   `data`, so this page reads it with Axios rather than useAPI. With the ops
 *   flag off and no v2 row, the page says v2 is not enabled (R2). Reading the
 *   response and building the save body live in
 *   client-onboarding-v2-settings.helpers.js, where they are tested.
 *
 **********/

import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Grid,
  Card,
  Button,
  AuthContext,
  ViewContext,
  Loader,
  Link,
  useAPI,
} from "components/lib";
import Axios from "axios";
import {
  V2_SETTINGS_URL,
  CHECK_TYPE_OPTIONS,
  V2_ENTITY_TYPES,
  APPROVAL_MODES,
  XPM_RELATIONSHIP_TYPES,
  SKIP_RELATIONSHIP,
  V2_RELATIONSHIP_ROLES,
  V2_FIELD_MAPPING_TYPES,
  readSettingsResponse,
  formStateFromSettings,
  buildV2SettingsPayload,
  toggleEntityType,
  relationshipTypeFor,
  setRelationshipType,
  fieldMappingFor,
  setFieldMapping,
  resetFieldMapping,
  formChoices,
  defaultFormId,
  v2PublicUrl,
  runExclusive,
  saveMayHaveStored,
  formIdAfterUnclearSave,
  redirectUrlError,
} from "./client-onboarding-v2-settings.helpers";

const TABS = [
  { key: "general", label: "General" },
  { key: "verification", label: "Verification" },
  { key: "xpm", label: "XPM Sync" },
  { key: "field_mappings", label: "Field Mappings" },
];

function Toggle({ checked, onChange, disabled = false }) {
  return (
    <label
      className={`relative inline-flex items-center ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      }`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only peer"
      />
      <div
        className={`w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-brand-500 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full ${
          disabled ? "opacity-50" : ""
        }`}
      ></div>
    </label>
  );
}

function StructureCheck({ read, chosenFormId }) {
  const savedFormId = read.misc.form_id || "";
  return (
    <div className="rounded border border-gray-200 px-3 py-3">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="font-medium">Form structure</h3>
          <p className="text-sm text-gray-500">
            Checks that the saved form has every question v2 needs.
          </p>
        </div>
        {read.formValid !== null && (
          <span
            className={`px-2 py-1 rounded-full text-xs font-medium ${
              read.formValid
                ? "bg-sg-green-bg text-sg-green"
                : "bg-sg-orange-bg text-sg-orange"
            }`}
          >
            {read.formValid ? "Valid" : "Problems found"}
          </span>
        )}
      </div>
      {read.formValid === null && (
        <p className="text-xs text-gray-400 mt-3">
          No form is saved yet. Choose a form above and save.
        </p>
      )}
      {read.formValid === false && (
        <>
          <ul className="mt-3 space-y-1 text-xs text-sg-orange">
            {read.formErrors.map((error, index) => (
              <li key={`${error.code}-${error.question}-${index}`}>
                {error.question && (
                  <span className="font-mono">{error.question}: </span>
                )}
                {error.message}
              </li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-2">
            The public link refuses a broken form. Fix the form in the Form
            Builder, or install a new copy of the template above and save.
          </p>
        </>
      )}
      {savedFormId && chosenFormId && chosenFormId !== savedFormId && (
        <p className="text-xs text-gray-400 mt-2">
          You chose another form. The check runs when you save.
        </p>
      )}
    </div>
  );
}

export function ClientOnboardingV2SettingsView() {
  const authContext = useContext(AuthContext);
  const viewContext = useContext(ViewContext);
  // The view context object is rebuilt on every render, so the loader reads it
  // through a ref rather than depending on it.
  const viewContextRef = useRef(viewContext);
  viewContextRef.current = viewContext;

  // Opting the firm in to sending tax file numbers to XPM is an admin decision.
  // The server carries the stored value forward for everyone else, so this only
  // decides whether the control is offered -- it is not the check that matters.
  const mayWriteTfn = authContext.permission?.admin === true;

  const [response, setResponse] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [form, setForm] = useState(null);
  const [activeTab, setActiveTab] = useState("general");
  const [saving, setSaving] = useState(false);
  // Set while a save is in flight, so a double click sends one POST
  // (runExclusive). `saving` only drives the button label.
  const savingRef = useRef(false);
  const [formsReload, setFormsReload] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await Axios.get(V2_SETTINGS_URL);
      setResponse(res.data);
      setForm(formStateFromSettings(res.data, { mayWriteTfn }));
    } catch (err) {
      setLoadFailed(true);
      viewContextRef.current.handleError(err);
    }
  }, [mayWriteTfn]);

  useEffect(() => {
    load();
  }, [load]);

  const read = readSettingsResponse(response);
  // The account's forms, for the form picker. Not fetched for an account that
  // cannot use the page. Fetched again after each save, because a template pick
  // installs a new copy that the picker must name.
  const formsAPI = useAPI(
    response && !read.notEnabled ? "/api/form" : null,
    "get",
    formsReload
  );
  const formsList = formsAPI.data;
  const savedFormId = read.misc.form_id || "";
  const available = read.available;

  // Before the first save the picker starts on the template, because the
  // account's forms load after the settings. When they arrive, start on the
  // account's newest v2 copy instead, if it has one (a Form catalog install,
  // say), so a save left unchanged keeps that copy. This runs when the list
  // arrives, so a pick made after that stands.
  useEffect(() => {
    if (!Array.isArray(formsList) || savedFormId) return;
    setForm(
      (prev) =>
        prev && {
          ...prev,
          formId: defaultFormId({ forms: formsList, savedFormId, available }),
        }
    );
  }, [formsList, savedFormId, available]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // After a save that may have stored (saveMayHaveStored), the stored row
  // becomes the next save's base, and the edits on screen stay.
  const refreshStored = async () => {
    try {
      const res = await Axios.get(V2_SETTINGS_URL);
      setResponse(res.data);
      setForm((prev) => prev && { ...prev, formId: formIdAfterUnclearSave(prev.formId, res.data) });
      setFormsReload((count) => count + 1);
    } catch (err) {
      // The next save reports its own error.
    }
  };

  const save = () =>
    runExclusive(savingRef, async () => {
      // The server drops a redirect that is not an absolute http(s) URL, so
      // stop here and say why rather than lose it without a word.
      const redirectError = redirectUrlError(form.redirectUrl);
      if (redirectError) {
        viewContext.notification.show(redirectError, "error", true);
        return;
      }
      setSaving(true);
      try {
        const res = await Axios.post(
          V2_SETTINGS_URL,
          buildV2SettingsPayload(form, response, { mayWriteTfn })
        );
        const dropped = res.data?.tfn_holds_dropped || 0;
        viewContext.notification.show(
          dropped > 0
            ? `Settings saved. The TFN write is now off, so ${dropped} held TFN${
                dropped === 1 ? " was" : "s were"
              } deleted.`
            : "Settings saved",
          "success",
          true
        );
        // Reload: a template pick saves the new copy's id, and the structure
        // check runs on the saved form. The forms list reloads after the
        // settings, so the new copy shows by name.
        await load();
        setFormsReload((count) => count + 1);
      } catch (err) {
        // Not handleError for every status: it navigates away on a 404, and here
        // a 404 only means the chosen form is gone.
        if (err?.response?.status === 401) {
          viewContext.handleError(err);
        } else {
          viewContext.notification.show(
            err?.response?.data?.message || "Failed to save settings",
            "error",
            true
          );
          // Still inside runExclusive, so no second save starts from the old row.
          if (saveMayHaveStored(err)) await refreshStored();
        }
      } finally {
        setSaving(false);
      }
    });

  if (!response || !form) {
    return loadFailed ? (
      <Grid>
        <h1 className="text-2xl font-bold">Client Onboarding v2 Settings</h1>
        <Card>
          <p className="text-sm text-gray-500">
            The settings could not be loaded. Refresh the page to try again.
          </p>
        </Card>
      </Grid>
    ) : (
      <Loader />
    );
  }

  if (read.notEnabled) {
    return (
      <Grid>
        <h1 className="text-2xl font-bold">Client Onboarding v2 Settings</h1>
        <Card>
          <h3 className="font-medium">Not enabled for this account</h3>
          <p className="text-sm text-gray-500 mt-1 mb-4">
            StackGo turns Client Onboarding v2 on for each account. Contact
            support to turn it on. Your Client Onboarding settings and link are
            not affected.
          </p>
          <Link
            url="/client-onboarding-settings"
            text="Back to Client Onboarding settings"
          />
        </Card>
      </Grid>
    );
  }

  const publicUrl = v2PublicUrl(window.location.origin, read.row?.id);
  const choices = formChoices({
    forms: formsList || [],
    savedFormId,
    available,
  });
  const xpmAutoCreate = form.provider === "xpm" && form.autoCreate;

  const setProvider = (provider) =>
    set(
      provider === "xpm"
        ? { provider }
        : {
            provider,
            autoCreate: false,
            addRelationships: false,
            // An admin opts in again after XPM is chosen again. A non-admin
            // cannot change it, so the stored value is left as it is.
            ...(mayWriteTfn ? { writeTfn: false } : {}),
          }
    );

  const setAutoCreate = (checked) =>
    set(
      checked
        ? { autoCreate: true }
        : {
            autoCreate: false,
            addRelationships: false,
            ...(mayWriteTfn ? { writeTfn: false } : {}),
          }
    );

  return (
    <Grid>
      <h1 className="text-2xl font-bold">Client Onboarding v2 Settings</h1>

      {!read.available && (
        <div className="rounded border border-sg-blue-line bg-sg-blue-05 px-3 py-2 text-sm text-sg-blue-ink">
          Client Onboarding v2 is turned off for this account, so the public link
          shows a closed message. You can still change these settings, so your
          existing client groups keep working.
        </div>
      )}

      {/* Tab navigation */}
      <div className="flex gap-1 border-b border-gray-200">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.key
                ? "border-brand-500 text-sg-blue"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* General Tab */}
      {activeTab === "general" && (
        <Card>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium">Enable the v2 form</h3>
                <p className="text-sm text-gray-500">
                  Allow clients to open the v2 form at its public URL. Your
                  Client Onboarding (v1) link is separate and keeps working.
                </p>
              </div>
              <Toggle
                checked={form.enabled}
                onChange={(checked) => set({ enabled: checked })}
              />
            </div>

            {publicUrl && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Public URL
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={publicUrl}
                    className="flex-1 border border-gray-300 rounded-control px-3 py-2 text-sm bg-gray-50"
                  />
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(publicUrl);
                      viewContext.notification.show("URL copied", "success", true);
                    }}
                    className="px-3 py-2 bg-btn-primary text-white rounded-control text-sm hover:bg-btn-primary-hover"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notification Email
              </label>
              <input
                type="email"
                value={form.notificationEmail}
                onChange={(e) => set({ notificationEmail: e.target.value })}
                placeholder="admin@yourfirm.com"
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Receives notification when a new onboarding form is submitted
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Redirect URL (after submission)
              </label>
              <input
                type="text"
                value={form.redirectUrl}
                onChange={(e) => set({ redirectUrl: e.target.value })}
                placeholder="https://yourfirm.com/thank-you"
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm"
              />
              <p className="text-xs text-gray-400 mt-1">
                Where clients go after they submit: a full address that starts
                with https://, or blank for the thank-you page.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Form
              </label>
              {choices.length > 0 ? (
                <select
                  value={form.formId}
                  onChange={(e) => set({ formId: e.target.value })}
                  className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm"
                >
                  {choices.map((choice) => (
                    <option key={choice.value} value={choice.value}>
                      {choice.label}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-gray-500">No form is saved.</p>
              )}
              <p className="text-xs text-gray-400 mt-1">
                Choose the template to install your own copy, then customise it
                in the Form Builder. A new copy replaces the form this link
                serves when you save.
              </p>
            </div>

            <StructureCheck read={read} chosenFormId={form.formId} />

            <div>
              <h3 className="font-medium mb-1">Organisation types</h3>
              <p className="text-sm text-gray-500 mb-3">
                The entity types clients can add. A sole trader is entered as a
                person onboarded as a client, so there is no Individual type
                here.
              </p>
              <div className="space-y-3">
                {V2_ENTITY_TYPES.map((type) => (
                  <label
                    key={type.value}
                    className="flex items-center gap-3 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={form.enabledEntityTypes.includes(type.value)}
                      onChange={() =>
                        set({
                          enabledEntityTypes: toggleEntityType(
                            form.enabledEntityTypes,
                            type.value
                          ),
                        })
                      }
                      className="w-4 h-4 text-sg-blue rounded border-gray-300"
                    />
                    <span className="text-sm">{type.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Verification Tab */}
      {activeTab === "verification" && (
        <Card>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Verification Check Type
              </label>
              <select
                value={form.checkType}
                onChange={(e) => set({ checkType: e.target.value })}
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm"
              >
                {CHECK_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-between rounded border border-gray-200 px-3 py-3">
              <div className="pr-4">
                <h4 className="font-medium">Send checks at submit</h4>
                <p className="text-sm text-gray-500">
                  Email each person who needs an identity check as soon as the
                  client submits. This is separate from XPM auto-create: checks
                  go out in both approval modes.
                </p>
              </div>
              <Toggle
                checked={form.autoVerify}
                onChange={(checked) => set({ autoVerify: checked })}
              />
            </div>

            <div className="rounded border border-sg-blue-line bg-sg-blue-05 px-3 py-2 text-sm text-sg-blue-ink">
              Checks go to each person onboarded as a client, each beneficial
              owner and partner, the CEO or senior managing official named for
              the group, and each contact person, except a contact person whose
              only roles are shareholdings under 25% or roles at a government
              body. A person with no email gets no check until staff add one.
            </div>
          </div>
        </Card>
      )}

      {/* XPM Sync Tab */}
      {activeTab === "xpm" && (
        <Card>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Provider
              </label>
              <select
                value={form.provider}
                onChange={(e) => setProvider(e.target.value)}
                className="w-full border border-gray-300 rounded-control px-3 py-2 text-sm"
              >
                <option value="">None</option>
                <option value="xpm">XPM (Xero Practice Manager)</option>
                <option value="karbon" disabled>
                  Karbon (Coming Soon)
                </option>
              </select>
            </div>

            {form.provider === "xpm" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="pr-4">
                    <h4 className="font-medium">Auto-create XPM records</h4>
                    <p className="text-sm text-gray-500">
                      Create an XPM client for each entity and each person
                      onboarded as a client. Contact persons become contacts
                      under their entity.
                    </p>
                  </div>
                  <Toggle checked={form.autoCreate} onChange={setAutoCreate} />
                </div>

                {xpmAutoCreate && (
                  <div className="rounded border border-gray-200 px-3 py-3">
                    <h4 className="font-medium mb-2">When to sync</h4>
                    <div className="space-y-3">
                      {APPROVAL_MODES.map((mode) => (
                        <label
                          key={mode.value}
                          className="flex items-start gap-3 cursor-pointer"
                        >
                          <input
                            type="radio"
                            name="approval_mode"
                            value={mode.value}
                            checked={form.approvalMode === mode.value}
                            onChange={() => set({ approvalMode: mode.value })}
                            className="w-4 h-4 mt-1 text-sg-blue border-gray-300"
                          />
                          <span className="text-sm">
                            {mode.label}
                            <span className="block text-gray-500">
                              {mode.description}
                            </span>
                          </span>
                        </label>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-3">
                      The XPM client group is named from the form&apos;s group
                      name question.
                    </p>
                  </div>
                )}

                {xpmAutoCreate && (
                  <div className="rounded border border-gray-200 px-3 py-3">
                    <div className="flex items-center justify-between">
                      <div className="pr-4">
                        <h4 className="font-medium">Write tax numbers to XPM</h4>
                        <p className="text-sm text-gray-500">
                          Write each entity&apos;s TFN into the XPM client&apos;s
                          Tax Number field, as a separate update after the
                          client is created. When staff approve first, the TFN
                          is held encrypted until approval, for a limited time;
                          after that time, staff write it by hand from the
                          client group.
                        </p>
                        {!mayWriteTfn && (
                          <p className="text-xs text-gray-400 mt-1">
                            Only an administrator can change this setting.
                          </p>
                        )}
                      </div>
                      <Toggle
                        checked={form.writeTfn}
                        disabled={!mayWriteTfn}
                        onChange={(checked) => set({ writeTfn: checked })}
                      />
                    </div>
                  </div>
                )}

                {xpmAutoCreate && (
                  <div className="rounded border border-gray-200 px-3 py-3">
                    <div className="flex items-center justify-between">
                      <div className="pr-4">
                        <h4 className="font-medium">Add client relationships</h4>
                        <p className="text-sm text-gray-500">
                          Write each role between two XPM clients as an XPM
                          relationship. A role held by a contact person is
                          kept here but not sent, because an XPM contact
                          cannot be a relationship endpoint.
                        </p>
                      </div>
                      <Toggle
                        checked={form.addRelationships}
                        onChange={(checked) => set({ addRelationships: checked })}
                      />
                    </div>

                    {form.addRelationships && (
                      <div className="mt-4 border-t border-gray-100 pt-3">
                        <p className="text-sm text-gray-500 mb-2">
                          XPM relationship type for each role. Defaults are
                          shown; roles with no exact XPM equivalent are mapped
                          to the closest available.
                        </p>
                        <div className="space-y-2">
                          {V2_RELATIONSHIP_ROLES.map((role) => (
                            <div
                              key={role.value}
                              className="flex items-center justify-between gap-3"
                            >
                              <span className="text-sm text-gray-700">
                                {role.label}
                              </span>
                              <select
                                value={relationshipTypeFor(
                                  form.relationshipTypeMap,
                                  role
                                )}
                                onChange={(e) =>
                                  set({
                                    relationshipTypeMap: setRelationshipType(
                                      form.relationshipTypeMap,
                                      role.value,
                                      e.target.value
                                    ),
                                  })
                                }
                                className="border border-gray-300 rounded px-2 py-1 text-sm"
                              >
                                {XPM_RELATIONSHIP_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                                <option value={SKIP_RELATIONSHIP}>
                                  Skip (don't sync)
                                </option>
                              </select>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Field Mappings Tab */}
      {activeTab === "field_mappings" && (
        <Card>
          <h3 className="font-medium mb-2">PMS Field Mappings</h3>
          {form.provider ? (
            <>
              <p className="text-sm text-gray-500 mb-4">
                The left-hand name is the XPM field the value is written to; the
                box holds a template, where{" "}
                <code className="bg-gray-100 px-1 rounded">{"{fieldName}"}</code>{" "}
                is replaced by that question&apos;s answer. A type you have not
                changed uses the standard mapping.
              </p>
              <div className="mb-6 rounded bg-sg-blue-05 border border-sg-blue-line px-3 py-2 text-xs text-sg-blue-ink">
                <strong>Tax numbers are not set here.</strong> In XPM,{" "}
                <span className="font-mono">BusinessNumber</span> is the{" "}
                <strong>ABN</strong>. The <strong>TFN</strong> goes to the
                separate Tax Number field, only through{" "}
                <strong>Write tax numbers to XPM</strong> on the XPM Sync tab.
                The <strong>Person</strong> mapping is used for each person
                onboarded as a client; a contact person takes only Name, Email
                and Position from it.
              </div>

              {V2_FIELD_MAPPING_TYPES.map((type) => {
                const { mappings, customised } = fieldMappingFor(
                  form.fieldMappings,
                  type.value
                );
                return (
                  <div key={type.value} className="mb-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-medium text-sg-blue">
                        {type.label}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          customised
                            ? "bg-sg-orange-bg text-sg-orange"
                            : "bg-gray-100 text-gray-500"
                        }`}
                      >
                        {customised ? "Customised" : "Standard"}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(mappings).map(([pmsField, template]) => (
                        <div key={pmsField} className="flex gap-2 items-center">
                          <span className="w-40 text-xs text-gray-500 font-mono">
                            {pmsField}
                          </span>
                          <input
                            type="text"
                            value={template}
                            onChange={(e) =>
                              set({
                                fieldMappings: setFieldMapping(
                                  form.fieldMappings,
                                  type.value,
                                  pmsField,
                                  e.target.value
                                ),
                              })
                            }
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                          />
                        </div>
                      ))}
                    </div>
                    {customised && (
                      <button
                        onClick={() =>
                          set({
                            fieldMappings: resetFieldMapping(
                              form.fieldMappings,
                              type.value
                            ),
                          })
                        }
                        className="text-xs text-sg-blue hover:underline mt-1"
                      >
                        Use the standard mapping
                      </button>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <p className="text-sm text-gray-400">
              Select a PMS provider on the XPM Sync tab to configure field
              mappings.
            </p>
          )}
        </Card>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <Button
          text={saving ? "Saving..." : "Save Settings"}
          action={save}
        />
      </div>
    </Grid>
  );
}

export default ClientOnboardingV2SettingsView;
